import React, { useState, useEffect, useContext } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import { ErrorContext } from '../../../ErrorContext';
import { LoadingCard } from 'src/commons/components/Card';
import {
  ApplicationStatus,
  ApplicationWorkflowEvent,
  BankConnectionAuthorizationLegalDocument,
  CARDSTAR_OVERRIDE_ACCOUNT_CREATION,
  GraphQLErrorCodes,
  PageNames,
  SET_PAYMENT_DATE_OPTIMIZELY_KEY,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import {
  GraphQLResponse,
  ApplicationLegalDocument,
} from 'src/commons/data/graphql';
import {
  AutoPayPreferencesPayload,
  createCreditCardApplicationAutopayPreferences,
  getCreditCardApplicationDocuments,
  getCreditCardLegalDocuments,
  processCreditCardApplicationWorkflowEvent,
} from 'src/commons/data/queries';
import { ifFeatureFlagOn } from 'src/commons/utilities/feature-flags';
import {
  logError,
  nrTrackCreditCardApplicationWorkflowEvent,
} from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';

import { AutoPayPreferences } from './AutoPayPreferences';
import { AutoPaySetupConditional } from './AutoPaySetup/AutoPaySetupConditional/AutoPaySetupConditional';
import { AutoPaySetupUnconditional } from './AutoPaySetup/AutoPaySetupUnconditional/AutoPaySetupUnconditional';

/**
 * Main AutoPay component
 * orchestrates the autopay process, switches out the internal pages
 */

export const AutoPay = () => {
  // Context --------------------------------------------------------------------------------------
  const {
    creditCardApplication,
    fetchCreditCardApplication,
    fetchAndResumeCreditCardApplication,
    fetchingApplication,
    showApplicationFetchError,
  } = useContext(CreditCardApplicationContext);
  const { setShowErrorModal } = useContext(ErrorContext);
  const { applicationStatus, selectedBankAccount } = creditCardApplication;
  const [failedBankName, setFailedBankName] = useState<string | null>(null);
  const [authorizedPlaidLink, setAuthorizedPlaidLink] = useState(false);
  const [plaidShouldBeOpen, setPlaidShouldBeOpen] = useState(false);
  const [checkingSavedLegalDocs, setCheckingSavedLegalDocs] = useState(false);
  const [fetchingLatestBcaDoc, setFetchingLatestBcaDoc] = useState(false);
  const [showDocFetchFailure, setShowDocFetchFailure] = useState(false);
  const [consentInProgress, setConsentInProgress] = useState(false);
  const [bcaDocument, setBcaDocument] =
    useState<BankConnectionAuthorizationLegalDocument>({
      name: '',
      title: '',
      version: 0,
      viewUrl: '',
    });
  const [submittingApplication, setSubmittingApplication] =
    useState<boolean>(false);
  let bankName = '';
  let trimmedBankName = '';
  let maskedAccountNumber = '';
  if (selectedBankAccount) {
    ({ bankName, maskedAccountNumber } = selectedBankAccount);
    trimmedBankName = bankName ? bankName.split(' - ')[0] : '';
  }

  // Hooks ----------------------------------------------------------------------------------------
  const navigate = useNavigate();
  const { applicationId } = useParams();

  // Functions ------------------------------------------------------------------------------------

  const showSetupPage = () => {
    navigate(prefixRoute(`/application/${applicationId}/auto-pay/setup`), {
      replace: true,
    });
  };

  // ApplicationRouteNavigate from the setup page to the preferences page
  const showPreferencesPage = () => {
    navigate(prefixRoute(`/application/${applicationId}/auto-pay/preferences`));
  };

  // Route to ExportWaitingRoom page when AutoPay page is submitted for conditional apps
  const showExportWaitingRoomPage = () => {
    navigate(prefixRoute(`/application/${applicationId}/export-waiting-room`));
  };

  // Route to PaymentDate page when AutoPay is skipped
  const showPaymentDatePage = () => {
    navigate(prefixRoute(`/application/${applicationId}/payment-date`));
  };

  const isPendingUnconditionalSubmission = () =>
    applicationStatus &&
    applicationStatus === ApplicationStatus.PENDING_UNCONDITIONAL_SUBMISSION;

  const submitAutoPayPreferences = async (
    payload: AutoPayPreferencesPayload
  ): Promise<GraphQLResponse> => {
    const res = await createCreditCardApplicationAutopayPreferences(
      applicationId,
      payload
    );
    // check here that you have all necessary fields coming back
    if (
      !(
        res &&
        res.data &&
        res.data.createCreditCardApplicationAutopayPreferences
      )
    ) {
      const err = new Error('Failed to save AutoPay preferences');
      throw err;
    }
    return res;
  };

  // Clear unfinished abandoned oauth query param if user quits Plaid early
  const handlePlaidExit = () => {
    // Always set firstConsent false in case this is the first Plaid Link session for the user
    setPlaidShouldBeOpen(false);
    // Check URL if exit occurred during OAuth reinitialization step
    if (/oauth_state_id/.test(window.location.search)) {
      // Remove OAuth query parameter to restart from initial step of choosing an institution
      showSetupPage();
    }
  };

  // Process workflow event BANK_CONNECT_AUTHORIZED when the user clicks 'Set up AutoPay'
  const processWorkflowEventBankConnectAuthorized = async (
    bcaDocName: string,
    bcaDocVersion: number
  ) => {
    setConsentInProgress(true);
    try {
      const workflowEvent = ApplicationWorkflowEvent.BANK_CONNECT_AUTHORIZED;
      const workflowEventAttributes = {
        legalDocumentsToSave: [
          {
            name: bcaDocName,
            version: bcaDocVersion,
          },
        ],
      };
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        workflowEvent,
        workflowEventAttributes
      );
      const { data, errors } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (data && data.processCreditCardApplicationWorkflowEvent) {
        // Update credit card application in context after successful workflow event
        setAuthorizedPlaidLink(true);
        setPlaidShouldBeOpen(true);
      } else if (errors) {
        // TODO: handle GQL errors
        setShowErrorModal(true);
      }
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setShowErrorModal(true);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'AutoPay',
          function: 'processWorkflowEventBankConnectAuthorized',
        });
      }
    } finally {
      setConsentInProgress(false);
    }
  };

  // Save Bank Connection Authorization doc if AutoPay enrolled in PENDING_UNCONDITIONAL_SUBMISSION
  const authorizeBankConnection = async () => {
    // Skip if authorization already occurred
    if (authorizedPlaidLink) return;
    // Authorize bank connection if the latest BCA legal doc was successfully retrieved
    if (bcaDocument) {
      const { name, version } = bcaDocument;
      processWorkflowEventBankConnectAuthorized(name, version);
    } else {
      // Show error if the latest BCA legal doc is not available to save on the application
      setShowDocFetchFailure(true);
    }
  };

  // Process workflow event BANK_CONNECTED when the user finishes Plaid successfully
  const processWorkflowEventBankConnected = async (
    publicToken: string,
    accountId: string,
    institutionName?: string
  ) => {
    setFailedBankName(null);
    try {
      const workflowEvent = ApplicationWorkflowEvent.BANK_CONNECTED;
      const workflowEventAttributes = {
        plaidPublicToken: publicToken,
        plaidSelectedBankAccountId: accountId,
      };
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        workflowEvent,
        workflowEventAttributes
      );
      const { data, errors } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (data && data.processCreditCardApplicationWorkflowEvent && !errors) {
        // Fetch fresh application in context to properly redirect to AutoPay prefs
        fetchCreditCardApplication();
      } else if (
        errors &&
        errors.length > 0 &&
        errors[0].code &&
        errors[0].code === GraphQLErrorCodes.PLAID_SELECTED_ACCOUNT_NOT_FOUND
      ) {
        setFailedBankName(institutionName);
      } else {
        setShowErrorModal(true);
      }
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setShowErrorModal(true);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'AutoPay',
          function: 'processWorkflowEventBankConnected',
        });
      }
    }
  };

  // Process workflow event APPLICATION_SUBMITTED when the user saves AutoPay prefs (unconditional)
  const processWorkflowEventApplicationSubmitted = async () => {
    setSubmittingApplication(true);
    try {
      const workflowEvent = ApplicationWorkflowEvent.APPLICATION_SUBMITTED;
      const applicationWorkflowEventAttributes = {
        onboardingOverride: ifFeatureFlagOn(CARDSTAR_OVERRIDE_ACCOUNT_CREATION),
      };
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        workflowEvent,
        applicationWorkflowEventAttributes
      );
      const { data } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (data && data.processCreditCardApplicationWorkflowEvent) {
        // Route to ExportWaitingRoom page if status was successfully transitioned
        showExportWaitingRoomPage();
      } else {
        setShowErrorModal(true);
        setSubmittingApplication(false);
      }
    } catch (err: any) {
      setSubmittingApplication(false);
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setShowErrorModal(true);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'AutoPay',
          function: 'processWorkflowEventApplicationSubmitted',
        });
      }
    }
  };

  // AutoPay declined saved
  const saveAutoPayPreferencesDeclined = async () => {
    const createAutoPayPreferencesPayload = {
      paymentAmountType: 'NO_AUTOPAY',
    };
    setSubmittingApplication(true);
    try {
      const res = await submitAutoPayPreferences(
        createAutoPayPreferencesPayload
      );
      // check here that you have all necessary fields coming back
      if (
        !(
          res &&
          res.data &&
          res.data.createCreditCardApplicationAutopayPreferences
        )
      ) {
        throw new Error('Failed to save AutoPay preferences');
      }
      if (
        res.data.createCreditCardApplicationAutopayPreferences
          .paymentAmountType !==
        createAutoPayPreferencesPayload.paymentAmountType
      ) {
        throw new Error('Response does not match sent values');
      }
      // Route to PaymentDate page when the FF is enabled
      if (ifFeatureFlagOn(SET_PAYMENT_DATE_OPTIMIZELY_KEY))
        showPaymentDatePage();
      // Route to ExportWaitingRoom depending on funnel type when the FF is disabled
      else processWorkflowEventApplicationSubmitted();
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setShowErrorModal(true);
        setSubmittingApplication(false);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'AutoPay',
          function: 'saveAutoPayPreferencesDeclined',
        });
      }
    }
  };

  // Effects

  useEffect(() => {
    fetchAndResumeCreditCardApplication(PageNames.AUTO_PAY);
  }, []);

  // Handle autopay for unconditional applications
  useEffect(() => {
    if (!fetchingApplication && isPendingUnconditionalSubmission()) {
      // Show AutoPayPreferences if bank was already connected for unconditional apps
      if (selectedBankAccount) showPreferencesPage();
      // Check if bank connection was already authorized for unconditional apps
      else setCheckingSavedLegalDocs(true);
    }
  }, [fetchingApplication, creditCardApplication, selectedBankAccount]);

  // Fetch legal docs saved to application to check for prior bank connection authorization
  useEffect(() => {
    const hasBcaDocument = async () => {
      try {
        // Check for prior authorization by determining whether the document has already been saved
        const { data, errors } =
          await getCreditCardApplicationDocuments(applicationId);
        // Show error if the legal doc request failed
        if (errors) setShowDocFetchFailure(true);
        // Check legal docs response for the BCA document
        else if (data && data.getCreditCardApplicationDocuments) {
          // Get the array of all docs currently saved to the application
          const { getCreditCardApplicationDocuments: savedDocs } = data;
          // Check if any doc in savedDocs array has classification BANK_CONNECTION_AUTHORIZATION
          const hasPriorBankConnectionAuthorization = await savedDocs.reduce(
            (hasSavedBcaDoc: boolean, doc: ApplicationLegalDocument) =>
              hasSavedBcaDoc ||
              doc.classification === 'BANK_CONNECTION_AUTHORIZATION',
            false
          );
          if (hasPriorBankConnectionAuthorization) setAuthorizedPlaidLink(true);
        }
      } catch (err: any) {
        if (err.message !== UNAUTHORIZED_MESSAGE) {
          setShowDocFetchFailure(true);
          logError(err, {
            errorName: err.name || 'PageError',
            errorMessage: err.message || 'Unexpected error occurred',
            component: 'AutoPay',
            function: 'hasBcaDocument',
          });
        }
      } finally {
        setFetchingLatestBcaDoc(true);
      }
    };
    if (checkingSavedLegalDocs) {
      hasBcaDocument();
    }
  }, [checkingSavedLegalDocs]);

  // Get legal document Bank Connection Authorization for unconditional apps
  useEffect(() => {
    const getLatestBcaDocument = async () => {
      try {
        // Fetch legal docs
        const response: GraphQLResponse =
          await getCreditCardLegalDocuments('BANK_CONNECTION');
        const { data, errors } = response;
        if (!errors && data && data.getCreditCardLegalDocuments) {
          setBcaDocument(data.getCreditCardLegalDocuments[0]);
        } else if (errors) {
          setShowDocFetchFailure(true);
        }
      } catch (err: any) {
        setShowDocFetchFailure(true);
        if (err.message !== UNAUTHORIZED_MESSAGE) {
          logError(err, {
            errorName: err.name || 'PageError',
            errorMessage: err.message || 'Unexpected error occurred',
            component: 'AutoPay',
            function: 'getLatestBcaDocument',
          });
        }
      } finally {
        setCheckingSavedLegalDocs(false);
        setFetchingLatestBcaDoc(false);
      }
    };
    if (checkingSavedLegalDocs && fetchingLatestBcaDoc) {
      getLatestBcaDocument();
    }
  }, [checkingSavedLegalDocs, fetchingLatestBcaDoc]);

  const getAutoPayRouteElement = (): JSX.Element => {
    if (fetchingApplication || !creditCardApplication || !applicationStatus) {
      return <LoadingCard />;
    }
    if (isPendingUnconditionalSubmission()) {
      return (
        <AutoPaySetupUnconditional
          loading={
            fetchingApplication ||
            checkingSavedLegalDocs ||
            fetchingLatestBcaDoc
          }
          showFetchError={showApplicationFetchError || showDocFetchFailure}
          failedBankName={failedBankName}
          authorized={authorizedPlaidLink}
          plaidShouldBeOpen={plaidShouldBeOpen}
          bcaDocument={bcaDocument}
          resumeAutoPay={() =>
            fetchAndResumeCreditCardApplication(PageNames.AUTO_PAY)
          }
          onPlaidConsent={authorizeBankConnection}
          onPlaidSuccess={processWorkflowEventBankConnected}
          onPlaidExit={handlePlaidExit}
          onPlaidOpenClick={() => setPlaidShouldBeOpen(true)}
          saveAutoPayPreferencesDeclined={saveAutoPayPreferencesDeclined}
          consentInProgress={consentInProgress}
          submitting={submittingApplication}
        />
      );
    }

    if (applicationStatus === ApplicationStatus.PENDING_SUBMISSION) {
      return (
        <AutoPaySetupConditional
          fetchingApplication={fetchingApplication}
          showApplicationFetchError={showApplicationFetchError}
          maskedAccountNumber={maskedAccountNumber}
          bankName={trimmedBankName}
          onSetupClick={showPreferencesPage}
          saveAutoPayPreferencesDeclined={saveAutoPayPreferencesDeclined}
          submitting={submittingApplication}
        />
      );
    }
    return <LoadingCard />;
  };

  return (
    <Routes>
      <Route path="/setup" element={getAutoPayRouteElement()} />
      <Route
        path="/preferences"
        element={
          fetchingApplication ? (
            <LoadingCard />
          ) : (
            <AutoPayPreferences
              showApplicationFetchError={showApplicationFetchError}
              setShowErrorModal={setShowErrorModal}
              applicationStatus={applicationStatus}
              selectedBankAccount={selectedBankAccount}
              showSetupPage={showSetupPage}
              saveAutoPayPreferencesEnrolled={submitAutoPayPreferences}
              saveAutoPayPreferencesDeclined={saveAutoPayPreferencesDeclined}
              submitAutoPayPage={processWorkflowEventApplicationSubmitted}
              submitting={submittingApplication}
            />
          )
        }
      />
      <Route
        path="/"
        element={
          <Navigate
            replace
            to={prefixRoute(`/application/${applicationId}/auto-pay/setup`)}
          />
        }
      />
    </Routes>
  );
};
